type RecordValue = Record<string, unknown>;

const asRecord = (value: unknown): RecordValue | undefined =>
  typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as RecordValue)
    : undefined;

const isStringOrAbsent = (value: unknown): boolean =>
  value === undefined || value === null || typeof value === 'string';

const hasListingCardFields = (value: RecordValue): boolean =>
  Boolean(
    value.address ||
    value.localization ||
    value.characteristics ||
    value.prices ||
    value.attachments ||
    value.id,
  );

const isLocalizationEntrySafe = (entry: unknown): boolean =>
  isStringOrAbsent(asRecord(asRecord(entry)?.text)?.title);

const isLocalizationSafe = (localization: RecordValue): boolean => {
  for (const [key, entry] of Object.entries(localization)) {
    if (key === 'primary' && !isStringOrAbsent(entry)) {
      return false;
    }
    if (key !== 'primary' && !isLocalizationEntrySafe(entry)) {
      return false;
    }
  }
  return true;
};

const isExternalIdsSafe = (externalIds: RecordValue): boolean =>
  Object.values(externalIds).every((value) => isStringOrAbsent(value));

const isListingCardFieldTypesSafe = (card: RecordValue): boolean => {
  const localization = asRecord(card.localization);
  const externalIds = asRecord(card.externalIds);

  return (
    (!localization || isLocalizationSafe(localization)) &&
    (!externalIds || isExternalIdsSafe(externalIds))
  );
};

/** Drops listing payloads whose nested card fields would break assign-row rendering. */
export const sanitizeRegionalHighlightAssignableListingPayload = (
  value: unknown,
): Record<string, unknown> | undefined => {
  const payload = asRecord(value);
  if (!payload) {
    return undefined;
  }

  const nestedListing = asRecord(payload.listing);
  const card = nestedListing ?? payload;
  if (!hasListingCardFields(card)) {
    return payload;
  }

  if (!isListingCardFieldTypesSafe(card)) {
    return undefined;
  }

  return payload;
};
